import axios, { AxiosRequestConfig, AxiosResponse } from 'axios'

import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { ID } from '@models/base'
import { UserModel } from '@models/user'
import { UserMeModel } from '@models/userMe'

import { API_URL } from '@/constants/constant'

export const USERS_URL = `${API_URL}/users`

export type ResponseUsers = Response<WithFilter<Array<UserModel>>>

export type ResponseUser = Response<{ user: UserModel }>

export type ResponseUserMe = Response<{ me: UserMeModel }>

export type QueryUsersFilter = {
  is_active?: boolean
  role_ids?: Array<ID>
}

export type PayloadVerifyProfileTwoFA = {
  otp: string
}

type PayloadUserUploadSignature = {
  file: File
}

export type PayloadFilterUsers = RequestQuery<{
  role_ids: Array<ID> | null
  is_active: boolean | null
}>

export interface IUsersAPI {
  filter: (query: PayloadFilterUsers) => Promise<ResponseUsers>

  get: (id: ID) => Promise<ResponseUser>

  getMe: () => Promise<ResponseUserMe>

  getOptionsForTaskForm: (query: RequestQuery) => Promise<ResponseUsers>

  getProfile: () => Promise<ResponseUser>

  generateProfileTwoFA: <D>(axiosConfig?: AxiosRequestConfig<D>) => Promise<Blob>

  verifyProfileTwoFA: <D extends PayloadVerifyProfileTwoFA>(
    payload: D,
    axiosConfig?: AxiosRequestConfig<D>
  ) => Promise<ResponseUser>

  getOptionsForBeginCashierSessionForm: (query: RequestQuery) => Promise<ResponseUsers>

  getOptionsForVoidSalesInvoiceForm: (query: RequestQuery) => Promise<ResponseUsers>

  getOptionsForEmailLoginForm: (query: RequestQuery) => Promise<ResponseUsers>

  uploadSignature: (payload: PayloadUserUploadSignature, config?: AxiosRequestConfig) => Promise<ResponseUser>

  downloadSignature: (config?: AxiosRequestConfig) => Promise<Blob>
}

export const UsersAPI: IUsersAPI = {
  getMe: async () => {
    return axios.get(`${USERS_URL}/me`).then((r: AxiosResponse) => r.data)
  },

  filter: async (query) => {
    return axios.post(`${USERS_URL}/filter`, query).then((r: AxiosResponse) => r.data)
  },

  get: async (id) => {
    return axios.get(`${USERS_URL}/${id}`).then((r: AxiosResponse) => r.data)
  },

  getOptionsForTaskForm: async (query) => {
    return axios.post(`${USERS_URL}/options/task-form`, query).then((r: AxiosResponse) => r.data)
  },

  getProfile: () => {
    return axios.get(`${USERS_URL}/profile`).then((r: AxiosResponse<ResponseUser>) => r.data)
  },

  generateProfileTwoFA: (axiosConfig) => {
    return axios
      .post(`${USERS_URL}/profile/2fa`, undefined, {
        responseType: 'blob',
        ...(axiosConfig && axiosConfig),
      })
      .then((r: AxiosResponse<Blob>) => r.data)
  },

  verifyProfileTwoFA: (payload, axiosConfig) => {
    return axios
      .post(`${USERS_URL}/profile/2fa/verify`, payload, axiosConfig ?? {})
      .then((r: AxiosResponse<ResponseUser>) => r.data)
  },

  getOptionsForBeginCashierSessionForm: async (query) => {
    return axios.post(`${USERS_URL}/options/begin-cashier-session-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForVoidSalesInvoiceForm: async (query) => {
    return axios.post(`${USERS_URL}/options/void-sales-invoice-form`, query).then((r: AxiosResponse) => r.data)
  },

  getOptionsForEmailLoginForm: async (query) => {
    return axios.post(`${USERS_URL}/options/email-login-form`, query).then((r: AxiosResponse) => r.data)
  },

  uploadSignature: async (payload) => {
    const formData = new FormData()
    formData.append('file', payload.file)
    return axios.post(`${USERS_URL}/profile/upload-signature`, formData, {
      headers: {
        'content-type': 'multipart/form-data',
      },
    })
  },

  downloadSignature: async (config) => {
    return axios
      .get(`${USERS_URL}/profile/download-signature`, {
        responseType: 'blob',
        ...config,
      })
      .then((r: AxiosResponse<Blob>) => r.data)
  },
}

export type { PayloadUserUploadSignature }
